<script setup lang="ts">
import { computed, ref } from "vue";

import Button from "~/components/dumb/Button.vue";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import ThinkingLevelDropdown from "~/components/dumb/ThinkingLevelDropdown.vue";
import { getConfiguredLocalAgent, getLocalAgentModelSettings } from "~/constants/localAgent";
import { getModelChipLabel, getResolvedModel, MODEL_TO_DEFAULT_THINKING_LEVEL } from "~/constants/model";
import { AiModel, AiThinkingLevel, ButtonSize, ButtonStyle, Placement } from "~/shared/enums";
import type { Chat } from "~/shared/types";
import { useDataStore } from "~/stores";
import buildAiModelDropdownSections from "~/utils/chat";

const props = defineProps<{
  chat: Chat;
  placement?: Placement;
}>();

const dataStore = useDataStore();

const dropdownMenuRef = ref<InstanceType<typeof DropdownMenu> | null>(null);

const currentChatModel = computed(() => getResolvedModel(props.chat.model));
const currentThinkingLevel = computed(() => props.chat.thinkingLevel);
const modelSettings = computed(() => {
  const targetAgent = props.chat.targetUserDuid ? dataStore.getUserByDuid(props.chat.targetUserDuid) : null;
  const localAgent = getConfiguredLocalAgent(targetAgent?.agent);
  const isLocalAgentChat = localAgent !== null;
  const localSettings = localAgent
    ? getLocalAgentModelSettings(
        localAgent,
        currentChatModel.value,
        currentThinkingLevel.value,
        targetAgent?.agent?.localState
      )
    : null;

  const showModelSwitcher = !isLocalAgentChat || (localSettings?.modelOptions.length ?? 0) > 1;
  const showThinkingLevelSwitcher = !isLocalAgentChat || (localSettings?.thinkingLevelOptions.length ?? 0) > 1;

  return {
    model: localSettings?.model ?? currentChatModel.value,
    thinkingLevel: localSettings?.thinkingLevel ?? currentThinkingLevel.value,
    modelOptions: localSettings?.modelOptions,
    thinkingLevelOptions: localSettings?.thinkingLevelOptions,
    localAgent: localAgent ?? undefined,
    isLocalAgentChat,
    showSwitcher: showModelSwitcher || showThinkingLevelSwitcher,
    showThinkingLevelSwitcher,
  };
});
const chipLabel = computed(() =>
  getModelChipLabel(
    modelSettings.value.model,
    modelSettings.value.thinkingLevelOptions?.length === 0 ? undefined : modelSettings.value.thinkingLevel
  )
);

const setModel = (model: AiModel) => {
  if (model === currentChatModel.value) {
    return;
  }
  dataStore.updateChat({
    duid: props.chat.duid,
    model,
    thinkingLevel: MODEL_TO_DEFAULT_THINKING_LEVEL[model],
  });
};

const setThinkingLevel = (level: AiThinkingLevel) => {
  if (level === currentThinkingLevel.value) {
    return;
  }
  dataStore.updateChat({ duid: props.chat.duid, model: modelSettings.value.model, thinkingLevel: level });
};

const dropdownSections = computed(() =>
  buildAiModelDropdownSections({
    currentModel: modelSettings.value.model,
    onModelSelect: setModel,
    models: modelSettings.value.modelOptions,
    allowAllProviders: modelSettings.value.isLocalAgentChat,
  })
);
</script>

<template>
  <DropdownMenu
    v-if="modelSettings.showSwitcher"
    ref="dropdownMenuRef"
    :sections="dropdownSections"
    :placement="placement ?? Placement.TOP_LEFT"
    class="min-w-0"
    :distance="0"
    :width-pixels="256"
    cover>
    <template #footer>
      <ThinkingLevelDropdown
        v-if="modelSettings.showThinkingLevelSwitcher"
        is-item
        :current-model="modelSettings.model"
        :current-thinking-level="modelSettings.thinkingLevel"
        :local-agent="modelSettings.localAgent"
        :on-thinking-level-select="setThinkingLevel" />
    </template>
    <Button
      :btn-style="ButtonStyle.CHIP"
      :size="ButtonSize.CHIP"
      :text="chipLabel"
      :data-on="dropdownMenuRef?.isOpenOrOpening"
      borderless
      text-style="text-xs text-vlt"
      class="min-w-0 px-1 !py-px on:bg-md" />
  </DropdownMenu>
  <span v-else class="px-1 text-xs text-vlt">{{ chipLabel }}</span>
</template>
